"use client";

import React, { useState, useRef, useEffect } from 'react';
import { Send, Trash2, Copy, ThumbsUp, ThumbsDown, Shield, Loader2 } from 'lucide-react';
import { useLanguage } from '@/lib/language-context';
import { t } from '@/lib/translations';
import { toast } from 'sonner';
import { auditLog } from '@/lib/supabase/audit-logger';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  tags?: string[];
  severity?: 'critical' | 'high' | 'medium' | 'info';
}

const initialMessages: Message[] = [
  {
    id: '1',
    role: 'assistant',
    content: `**Assalamu Alaikum. I am Otek1-Syber's AI Security Auditor**, powered by Gemini.\n\nI have full context of your current threat landscape:\n\n- **42 active threats** — 3 Critical, 11 High, 28 Medium\n- **Security Health Score: 74/100** — Yellow (Needs Attention)\n- **BD Compliance: 71%** — 2 critical sections non-compliant (§18, §29)\n\nI can help you:\n• Analyze active threats and recommend containment strategies\n• Audit your BD Cyber Security Act 2023 compliance gaps\n• Generate incident response runbooks\n• Explain MITRE ATT&CK techniques in your environment\n• Prepare reports for BTRC / government auditors\n\nWhat would you like to investigate first?`,
    timestamp: new Date(Date.now() - 300000),
    tags: ['System Context', 'BD Compliance', 'MITRE ATT&CK'],
    severity: 'info',
  },
  {
    id: '2',
    role: 'user',
    content: 'THR-2845 shows data exfiltration from DB-PROD-01. What is the likely attack vector and what should I do right now?',
    timestamp: new Date(Date.now() - 240000),
  },
  {
    id: '3',
    role: 'assistant',
    content: `**CRITICAL INCIDENT ANALYSIS — THR-2845**\n\n**Threat ID:** THR-2845 | **Technique:** T1041 (Exfiltration Over C2 Channel)\n\n---\n\n**Likely Attack Vector:**\n\nBased on the source IP (185.220.101.33 — Tor exit node, Germany) targeting Port 5432 (PostgreSQL), this is consistent with a **compromised application-layer credential** scenario:\n\n1. Attacker obtained DB credentials via earlier SQL Injection (see THR-2847)\n2. Established persistent connection through the application tier\n3. Executing slow-exfil to avoid volume-based detection\n\n**Estimated data at risk:** User PII table (~340K records), financial_transactions (last 90 days)\n\n---\n\n**🚨 Immediate Actions (Next 15 minutes):**\n\n1. **ISOLATE** — Block outbound from DB-PROD-01 to 185.220.101.0/24 at firewall level immediately\n2. **ROTATE** — All PostgreSQL credentials on DB-PROD-01 (svc_app, svc_backup, admin)\n3. **KILL** — Active sessions: \`SELECT pg_terminate_backend(pid) FROM pg_stat_activity WHERE client_addr = '185.220.101.33';\`\n4. **SNAPSHOT** — Take forensic snapshot of DB-PROD-01 before any remediation\n\n**BD Compliance Note:** Under §7 of the Bangladesh Cyber Security Act 2023, you have **24 hours** to report this incident to CIRT-BD (cirt@btrc.gov.bd).\n\nShould I generate the BTRC incident report template?`,
    timestamp: new Date(Date.now() - 230000),
    tags: ['T1041', 'T1190', 'Incident Response', 'BD §7'],
    severity: 'critical',
  },
  {
    id: '4',
    role: 'user',
    content: 'Yes, generate the BTRC incident report. Also what is our compliance status for Section 18 — I see it is failing.',
    timestamp: new Date(Date.now() - 180000),
  },
  {
    id: '5',
    role: 'assistant',
    content: `**BTRC INCIDENT REPORT TEMPLATE — THR-2845**\n\n---\n\`\`\`\nTO: CIRT-BD, Bangladesh Telecommunication Regulatory Commission\nFROM: [Organization Name] — Security Operations Center\nDATE: 17 March 2026, 04:11 IST\nSUBJECT: Cyber Incident Report — Data Exfiltration Attempt\n\nINCIDENT CLASSIFICATION: Critical — Data Breach Attempt\nTHREAT ID: THR-2845\nAFFECTED ASSET: DB-PROD-01 (PostgreSQL 14.2)\nATTACK VECTOR: External — Credential Abuse + C2 Exfil\nSOURCE IP: 185.220.101.33 (Tor Exit Node — DE)\nDETECTION TIME: 17-03-2026 03:49:22 BDT\nCONTAINMENT STATUS: In Progress\n\nDATA POTENTIALLY AFFECTED:\n- User records: ~340,000 entries\n- Transaction data: 90-day window\n- PII categories: Name, NID, phone, address\n\nACTIONS TAKEN: [To be filled by SOC]\nREPORTING OFFICER: [Name, Designation, NID]\n\`\`\`\n\n---\n\n**SECTION 18 COMPLIANCE GAP ANALYSIS:**\n\n§18 requires **12-month audit log retention** with tamper-proof storage. Your current score: **41%**\n\n**Gaps identified:**\n- ❌ Log retention: 47 days (required: 365 days)\n- ❌ No WORM storage — logs are mutable\n- ❌ Missing log integrity hashing (SHA-256 chain)\n- ⚠️ Log shipping to secondary site: configured but untested\n\n**Remediation (Priority: HIGH):**\n1. Increase log retention to 365 days in your SIEM configuration\n2. Enable S3 Object Lock (WORM) on log bucket — estimated cost: ~৳12,000/month\n3. Implement log signing with SHA-256 using \`rsyslog + imfile + mmhashsha256\`\n4. Schedule quarterly log integrity verification\n\nEstimated time to compliance: **14 business days**\nBD Act penalty for §18 non-compliance: Up to ৳50 Lakh or imprisonment.`,
    timestamp: new Date(Date.now() - 160000),
    tags: ['BTRC Report', 'BD §18', 'Log Retention', 'WORM'],
    severity: 'high',
  },
  {
    id: '6',
    role: 'user',
    content: 'What other critical vulnerabilities should I prioritize this week?',
    timestamp: new Date(Date.now() - 60000),
  },
  {
    id: '7',
    role: 'assistant',
    content: `**WEEKLY PRIORITY VULNERABILITY BRIEF — Week 11, 2026**\n\nBased on your asset inventory and active threat data:\n\n**🔴 P0 — Fix within 24 hours:**\n\n1. **CVE-2025-3194** (CVSS 9.8) — Apache HTTP Server 2.4.58 RCE on WEB-PORTAL-01\n   - Patch: Apache 2.4.63 available\n   - Risk: Remote code execution without authentication\n\n2. **CVE-2026-0847** (CVSS 9.1) — PostgreSQL 14.2 privilege escalation\n   - Directly related to active THR-2845\n   - Patch: PostgreSQL 14.12\n\n**🟠 P1 — Fix within 72 hours:**\n\n3. **CVE-2025-44228** (CVSS 8.7) — Log4Shell variant in your Java microservices (3 services affected)\n4. **Misconfiguration** — S3 bucket 'btrc-backup-2024' has public read access (PII exposure risk)\n\n**🟡 P2 — Fix within 2 weeks:**\n\n5. Expired TLS certificates on 4 internal services (expiring in 8 days)\n6. Default credentials on BTRC-SWITCH-07 (Cisco IOS — admin/admin)\n\nShall I generate automated remediation runbooks for any of these?`,
    timestamp: new Date(Date.now() - 45000),
    tags: ['CVE-2025-3194', 'CVE-2026-0847', 'CVE-2025-44228', 'Remediation'],
    severity: 'high',
  },
];

const suggestedPrompts = [
  'Generate BTRC incident report for THR-2845',
  'Explain MITRE T1190 in our environment',
  'What is our BD §18 compliance gap?',
  'Runbook for SQL Injection containment',
  'Weekly threat summary for CISO briefing',
  'Analyze brute force pattern from 45.142.212.8',
];

export default function ChatPanel() {
  const { language } = useLanguage();
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    // Log AI query to audit trail
    auditLog.aiQuerySubmitted();

    // Backend integration point: replace with actual Gemini API call
    // await geminiClient.generateContent({ prompt: input, context: threatContext })
    await new Promise(resolve => setTimeout(resolve, 2000));

    const aiMsg: Message = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: `I've analyzed your query regarding: **"${userMsg.content}"**\n\nBased on your current threat landscape and the Bangladesh Cyber Security Act 2023 requirements, I recommend the following course of action:\n\n1. **Immediate Investigation** — Cross-reference with MITRE ATT&CK framework for relevant TTPs\n2. **Compliance Check** — Verify this aligns with §4 (Data Protection) and §7 (Incident Reporting) requirements\n3. **Documentation** — Ensure all findings are logged for the mandatory audit trail under §18\n\nFor a more specific analysis, please provide additional context about the affected systems or the specific compliance section you're concerned about.\n\n*Note: This response is advisory. Always verify with your security team before taking action.*`,
      timestamp: new Date(),
      tags: ['Analysis', 'BD Compliance'],
      severity: 'info',
    };

    setMessages(prev => [...prev, aiMsg]);
    setIsLoading(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCopy = (content: string) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard');
  };

  const handleClearChat = () => {
    setMessages([initialMessages[0]]);
    toast.success('Chat cleared');
  };

  const formatContent = (content: string) => {
    return content
      .split('\n')
      .map((line, i) => {
        if (line.startsWith('**') && line.endsWith('**') && line.length > 4) {
          const text = line.slice(2, -2);
          return <p key={i} className="font-bold text-white mb-1">{text}</p>;
        }
        if (line.startsWith('```') || line.endsWith('```')) {
          return null;
        }
        if (line.startsWith('- ') || line.startsWith('• ')) {
          return (
            <div key={i} className="flex items-start gap-2 mb-0.5">
              <span className="text-cyber-blue mt-1 shrink-0">•</span>
              <span dangerouslySetInnerHTML={{ __html: formatInline(line.slice(2)) }} />
            </div>
          );
        }
        if (line.match(/^\d+\. /)) {
          const num = line.match(/^(\d+)\. /)?.[1];
          return (
            <div key={i} className="flex items-start gap-2 mb-0.5">
              <span className="text-cyber-blue font-mono text-[11px] mt-0.5 shrink-0 w-4">{num}.</span>
              <span dangerouslySetInnerHTML={{ __html: formatInline(line.replace(/^\d+\. /, '')) }} />
            </div>
          );
        }
        if (line === '---') {
          return <hr key={i} className="border-cyber-border my-2" />;
        }
        if (line.startsWith('`') && line.endsWith('`') && !line.startsWith('```')) {
          return (
            <code key={i} className="block bg-black/40 border border-cyber-border rounded px-3 py-2 font-mono text-[11px] text-cyber-green my-1 whitespace-pre-wrap">
              {line.slice(1, -1)}
            </code>
          );
        }
        if (line === '') return <div key={i} className="h-1" />;
        return <p key={i} className="mb-0.5" dangerouslySetInnerHTML={{ __html: formatInline(line) }} />;
      })
      .filter(Boolean);
  };

  const formatInline = (text: string) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
      .replace(/`(.*?)`/g, '<code class="bg-black/40 text-cyber-green font-mono text-[11px] px-1.5 py-0.5 rounded border border-cyber-border">$1</code>');
  };

  const severityBg: Record<string, string> = {
    critical: 'border-l-2 border-l-cyber-red',
    high: 'border-l-2 border-l-orange-400',
    medium: 'border-l-2 border-l-yellow-400',
    info: '',
  };

  return (
    <div className="cyber-card flex flex-col h-full overflow-hidden">
      {/* Chat Header */}
      <div className="px-4 py-3 border-b border-cyber-border flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-cyber-green rounded-full live-dot" />
          <span className="text-xs font-mono text-cyber-green">Gemini 1.5 Pro · Active Session</span>
        </div>
        <button
          onClick={handleClearChat}
          aria-label="Clear chat history"
          className="flex items-center gap-1.5 text-[11px] text-cyber-muted hover:text-cyber-red transition-colors"
        >
          <Trash2 size={12} />
          <span className={language === 'bn' ? 'font-bangla' : ''}>{t(language, 'ai_clear')}</span>
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto scrollbar-cyber px-4 py-4 space-y-4">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
          >
            {/* Avatar */}
            <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
              msg.role === 'assistant' ?'bg-cyber-blue/15 border border-cyber-blue/30' :'bg-cyber-purple/15 border border-cyber-purple/30'
            }`}>
              {msg.role === 'assistant'
                ? <Shield size={13} className="text-cyber-blue" />
                : <span className="text-[10px] font-bold text-cyber-purple">RA</span>
              }
            </div>

            {/* Bubble */}
            <div className={`max-w-[85%] group ${msg.role === 'user' ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
              {msg.role === 'assistant' && msg.severity && (
                <div className="flex items-center gap-1.5 mb-0.5">
                  {msg.severity === 'critical' && (
                    <span className="cyber-badge-critical text-[9px]">CRITICAL ANALYSIS</span>
                  )}
                  {msg.severity === 'high' && (
                    <span className="cyber-badge-high text-[9px]">HIGH PRIORITY</span>
                  )}
                </div>
              )}

              <div
                className={`
                  rounded-xl px-4 py-3 text-[13px] leading-relaxed
                  ${msg.role === 'user' ?'bg-cyber-blue/15 border border-cyber-blue/25 text-cyber-text rounded-tr-sm'
                    : `bg-[#131620] border border-cyber-border text-cyber-text rounded-tl-sm ${msg.severity ? severityBg[msg.severity] || '' : ''}`
                  }
                `}
              >
                {msg.role === 'assistant' ? (
                  <div className="space-y-0.5">{formatContent(msg.content)}</div>
                ) : (
                  <p>{msg.content}</p>
                )}
              </div>

              {/* Tags & Actions */}
              <div className={`flex items-center gap-2 flex-wrap ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <span className="text-[10px] text-cyber-muted font-mono">
                  {msg.timestamp.toLocaleTimeString('en-BD', { hour: '2-digit', minute: '2-digit' })}
                </span>
                {msg.tags?.map((tag) => (
                  <span key={tag} className="text-[9px] font-mono bg-cyber-blue/10 text-cyber-blue border border-cyber-blue/20 rounded px-1.5 py-0.5">
                    {tag}
                  </span>
                ))}
                {msg.role === 'assistant' && (
                  <div className="hidden group-hover:flex items-center gap-1">
                    <button onClick={() => handleCopy(msg.content)} aria-label="Copy message" className="p-1 hover:bg-white/5 rounded transition-colors">
                      <Copy size={10} className="text-cyber-muted hover:text-cyber-text" />
                    </button>
                    <button aria-label="Thumbs up" className="p-1 hover:bg-white/5 rounded transition-colors">
                      <ThumbsUp size={10} className="text-cyber-muted hover:text-cyber-green" />
                    </button>
                    <button aria-label="Thumbs down" className="p-1 hover:bg-white/5 rounded transition-colors">
                      <ThumbsDown size={10} className="text-cyber-muted hover:text-cyber-red" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex gap-3">
            <div className="w-7 h-7 rounded-full bg-cyber-blue/15 border border-cyber-blue/30 flex items-center justify-center shrink-0">
              <Shield size={13} className="text-cyber-blue" />
            </div>
            <div className="bg-[#131620] border border-cyber-border rounded-xl rounded-tl-sm px-4 py-3">
              <div className="flex items-center gap-2">
                <Loader2 size={13} className="text-cyber-blue animate-spin" />
                <span className={`text-[12px] text-cyber-muted ${language === 'bn' ? 'font-bangla' : ''}`}>
                  {t(language, 'ai_thinking')}
                </span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Suggested Prompts */}
      <div className="px-4 py-2 border-t border-cyber-border/50">
        <div className="flex gap-2 overflow-x-auto scrollbar-cyber pb-1" role="list" aria-label="Suggested prompts">
          {suggestedPrompts.map((prompt) => (
            <button
              key={prompt}
              onClick={() => setInput(prompt)}
              role="listitem"
              aria-label={`Use suggested prompt: ${prompt}`}
              className="shrink-0 text-[10px] font-mono text-cyber-muted border border-cyber-border rounded-lg px-2.5 py-1.5 hover:bg-cyber-blue/10 hover:border-cyber-blue/30 hover:text-cyber-blue transition-all duration-150 whitespace-nowrap"
            >
              {prompt}
            </button>
          ))}
        </div>
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-cyber-border shrink-0">
        <div className="flex items-end gap-3">
          <div className="flex-1 bg-[#131620] border border-cyber-border rounded-xl focus-within:border-cyber-blue/40 transition-colors px-4 py-3">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t(language, 'ai_placeholder')}
              rows={1}
              aria-label="Type your security question or command"
              aria-multiline="true"
              className={`w-full bg-transparent text-sm text-cyber-text placeholder-cyber-muted outline-none resize-none scrollbar-cyber max-h-32 ${language === 'bn' ? 'font-bangla' : ''}`}
              style={{ fontFamily: language === 'bn' ? "'Noto Sans Bengali', sans-serif" : "'DM Sans', sans-serif" }}
            />
            <div className="flex items-center justify-between mt-2">
              <p className={`text-[10px] text-cyber-subtle ${language === 'bn' ? 'font-bangla' : ''}`}>
                {t(language, 'ai_disclaimer')}
              </p>
              <span className="text-[10px] text-cyber-subtle font-mono">⏎ Send</span>
            </div>
          </div>

          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            aria-label={isLoading ? 'Sending message...' : 'Send message'}
            className={`
              w-10 h-10 rounded-xl flex items-center justify-center shrink-0
              transition-all duration-150 active:scale-95
              ${input.trim() && !isLoading
                ? 'bg-cyber-blue hover:bg-cyber-blue/80 text-white shadow-[0_0_15px_rgba(0,163,255,0.3)]'
                : 'bg-cyber-surface border border-cyber-border text-cyber-subtle cursor-not-allowed'
              }
            `}
          >
            {isLoading
              ? <Loader2 size={16} className="animate-spin" />
              : <Send size={16}/>
            }
          </button>
        </div>
      </div>
    </div>
  );
}